import { Button } from "@svea/design-system-components-react";
import {
	ButtonGroup,
	Dialog,
	FormWithTracking,
	formatSsn,
	Grid,
	isRequired,
	P,
	Stack,
	TextField,
	useText,
	validateEmail,
} from "@sveabank/cc-common";
import { useSelector } from "react-redux";
import Paper from "../../../components/paper";
import { selectPossibleGuarantors } from "../../../slices/guarantorSlice";
import type { AssignGuarantorEmailFormValues } from "../../../types/guarantor";
import GuarantorInfoBox from "../add-guarantor-box/guarantor-info-box";
import { filterCheckedGuarantors } from "../helpers";

type Props = {
	handleSend: (newSigners: AssignGuarantorEmailFormValues) => Promise<void>;
	isOpen: boolean;
	setOpen: (isOpen: boolean) => void;
	applicantNationalId: string;
};

const AddGuarantorEmailModal = ({ handleSend, isOpen, setOpen, applicantNationalId }: Props) => {
	const [t, texts] = useText();

	const possibleGuarantors = filterCheckedGuarantors(useSelector(selectPossibleGuarantors));

	return (
		<Dialog
			isOpen={isOpen}
			setOpen={setOpen}
			size="medium"
			center={false}
			heading={t(texts.signPage.assignEmailModal.titleGuarantor)}
		>
			<FormWithTracking
				initialValues={{ possibleGuarantors }}
				onSubmit={handleSend}
				matomoInstance={window.Matomo}
				render={({ handleSubmit, submitting }) => (
					<form onSubmit={handleSubmit} name="guarantorEmailForm">
						<Stack gap={4}>
							<Dialog.Content>
								<Stack gap={4}>
									<P>{t(texts.signPage.assignEmailModal.descriptionGuarantor)}</P>
									{possibleGuarantors.map((guarantor, index) => (
										<Paper key={guarantor.nationalId}>
											<Grid>
												<Grid.Col
													span={{
														base: 12,
														tablet: 3,
													}}
												>
													<GuarantorInfoBox
														firstName={guarantor.names.firstNames}
														lastName={guarantor.names.lastNames}
														ssn={formatSsn(guarantor.nationalId)}
													/>
												</Grid.Col>
												<Grid.Col
													span={{
														base: 12,
														tablet: 9,
													}}
												>
													<TextField
														disabled={guarantor.nationalId === applicantNationalId}
														label={t(texts.common.email)}
														type="email"
														name={`possibleGuarantors[${index}].email`}
														placeholder={t(texts.signPage.assignEmailModal.emailPlaceHolder)}
														fullWidth
														validators={[validateEmail, isRequired]}
													/>
												</Grid.Col>
											</Grid>
										</Paper>
									))}
								</Stack>
							</Dialog.Content>
							<Dialog.Footer>
								<ButtonGroup row>
									<Button variant="primary" type="submit" width="full" isPending={submitting}>
										{t(texts.signPage.assignEmailModal.sendButton)}
									</Button>
									<Button
										variant="secondary"
										type="button"
										onPress={() => setOpen(false)}
										width="full"
									>
										{t(texts.signPage.assignEmailModal.cancelButton)}
									</Button>
								</ButtonGroup>
							</Dialog.Footer>
						</Stack>
					</form>
				)}
			/>
		</Dialog>
	);
};

export default AddGuarantorEmailModal;
